/**
 * Extracts a user-friendly error message from various error response formats
 * @param {Error} error - The error object
 * @returns {string} A user-friendly error message
 */
export const extractErrorMessage = (error) => {
    let errorMessage = "An unknown error occurred"
  
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error
        }
  
        // Check if there's a more detailed error in the response
        if (error.response.data.errors) {
          const errors = error.response.data.errors
          if (Array.isArray(errors) && errors.length > 0) {
            // Join multiple errors if they exist
            errorMessage = errors.map((err) => err.msg || err.message || JSON.stringify(err)).join(", ")
          } else if (typeof errors === "object") {
            // Extract first error from object
            const firstError = Object.values(errors)[0]
            if (firstError) {
              errorMessage = typeof firstError === "string" ? firstError : JSON.stringify(firstError)
            }
          }
        }
      }
    } else if (error.message) {
      errorMessage = error.message
  
      // Check if it's a validation error
      if (error.message.includes("ValidationError")) {
        // Try to extract the specific field error
        const match = error.message.match(/validation failed: ([^:]+): (.+)/)
        if (match && match.length >= 3) {
          const field = match[1]
          const fieldError = match[2]
          errorMessage = `${field}: ${fieldError}`
        }
      }
    }
  
    return errorMessage
  }

/**
 * Formats validation errors for display in toast notifications
 * @param {Array} errors - Array of validation error messages
 * @returns {string} Formatted error message
 */
export const formatValidationErrors = (errors) => {
  if (!Array.isArray(errors) || errors.length === 0) {
    return "Validation failed";
  }
  
  if (errors.length === 1) {
    return errors[0];
  }
  
  return `Please fix the following errors:\n${errors.join('\n')}`;
}

/**
 * Extracts and formats error messages from API responses
 * @param {Error} error - The error object from API call
 * @returns {string} Formatted error message
 */
export const extractApiErrorMessage = (error) => {
  if (error.response?.data) {
    const responseData = error.response.data;
    
    // Handle new validation error format
    if (responseData.errors && Array.isArray(responseData.errors)) {
      return formatValidationErrors(responseData.errors);
    }
    
    // Handle single error message
    if (responseData.message) {
      return responseData.message;
    }
    
    // Handle legacy error format
    if (responseData.error) {
      return responseData.error;
    }
  }
  
  // Fallback to general error message
  return error.message || "An error occurred";
}
  